/**
 * The armature check on BYTES — the kernel's `rigCheck` fed from the same
 * import Harry's load door runs (`importGlb`: loadGLB → convertRig → mapRig).
 * Run on the bytes World handed over, and again on the bytes the intake export
 * writes, so the verdict Harry shows is always about a file, never about the
 * session's edited stage.
 *
 * A file with no skeleton (`NoSkeletonError`) is a finding (`no-skeleton`), not
 * a refusal: the guided mode must reach the person with it. Any other import
 * refusal is thrown with its own reason — the load door would refuse the same
 * file the same way.
 *
 * Depends on: kernel (rigCheck), adapter-three (NoSkeletonError), the import
 * pipeline. Depended on by: intake-session (via main.ts), tests.
 */
import { NoSkeletonError } from '@harryhausen/adapter-three';
import { rigCheck, rigCheckFileFacts, type RigCheckReport } from '@harryhausen/kernel';
import { importGlb, type ImportDeps } from '../import/import-pipeline.js';

export async function checkGlbForWorld(bytes: ArrayBuffer, deps: ImportDeps, label: string): Promise<RigCheckReport> {
  const file = rigCheckFileFacts(bytes);
  if (file === null) throw new Error(`${label} is not a readable binary glTF file`);
  try {
    // importGlb may hand the buffer to a worker-free loader that keeps a view; give it its own copy.
    const { rig } = await importGlb(bytes.slice(0), deps, { label });
    return rigCheck({ rig, ontology: deps.ontology, file });
  } catch (err) {
    if (err instanceof NoSkeletonError) return rigCheck({ rig: null, ontology: deps.ontology, file });
    throw err;
  }
}
